import { useState } from 'react';
import { C, COULEUR_DU_JOUR, POLICE_TITRE } from '../../theme';
import { Cadenas, ChevronBas, ChevronDroite, Coche } from '../../icones';
import { Squelette, Panne, Vide, Case } from '../composants/base';
import { api } from '../../api/routes';
import { useDonnees } from '../../api/useDonnees';
import { useCocher } from '../cocher';
import { aujourdhuiDans, depuisCombienDeJours, heureDe, jourDe, jourEnToutesLettres } from '../format';
import type { Theme } from '../theme-contexte';
import type { Evenement, JourPlanning } from '../../api/types';

/**
 * Aujourd'hui — une vue de LECTURE (handoff Horizon, § Ecrans 1).
 *
 * On y voit sa journee d'un regard, jamais le detail : chaque chose est une PILULE, et
 * toute pilule ouvre le Planning, l'endroit ou l'on agit. C'est le changement de fond de
 * cet ecran par rapport a l'ancienne version, qui montrait des cartes detaillees.
 */

/**
 * L'illustration qui correspond a la description rendue par le serveur.
 * Ce sont les icones du pack fourni par le porteur (Via_Weather_Icons), recadrees en
 * 96x96 et servies depuis /images/meteo/ (PAS /meteo/, qui est la route d API meteo).
 */
function imagePourMeteo(description: string): string {
  if (description.includes('dégagé')) return '/images/meteo/soleil.png';
  if (description.includes('peu nuageux')) return '/images/meteo/peu-nuageux.png';
  if (description.includes('couvert')) return '/images/meteo/couvert.png';
  if (description.includes('orage')) return '/images/meteo/orage.png';
  if (description.includes('neige')) return '/images/meteo/neige.png';
  if (description.includes('brouillard')) return '/images/meteo/brouillard.png';
  if (description.includes('averses')) return '/images/meteo/averses.png';
  if (description.includes('bruine')) return '/images/meteo/bruine.png';
  if (description.includes('pluie')) return '/images/meteo/pluie.png';
  return '/images/meteo/nuageux.png';
}

function accroche(nombre: number): string {
  if (nombre === 0) return 'Rien de prévu aujourd’hui.';
  if (nombre === 1) return 'Une seule chose au programme.';
  if (nombre <= 4) return 'Journée tranquille.';
  return 'Journée chargée, mais sous contrôle.';
}

/** La couleur fixe du jour d'un instant, dans le fuseau du foyer. */
function teinteDuJour(jour: string): string {
  // Le jour calendaire est ancre a midi UTC : il ne peut pas glisser d'un fuseau a
  // l'autre (meme regle que dans format.ts).
  return COULEUR_DU_JOUR[new Date(`${jour}T12:00:00Z`).getUTCDay()]!;
}

/** Ce qui reste a faire sur un evenement : ses elements de liste non coches. */
function resteAFaire(evenement: Evenement): { restants: number; total: number } {
  const elements = evenement.checklists.flatMap((liste) => liste.items);
  return {
    restants: elements.filter((element) => element.doneAt === null).length,
    total: elements.length,
  };
}

export function Aujourdhui({
  prenom,
  fuseau,
  version,
  signaler,
  rafraichir,
  theme,
  ouvrirPlanning,
}: {
  prenom: string;
  fuseau: string;
  version: number;
  signaler: (message: string) => void;
  rafraichir: () => void;
  theme: { libelle: string; resolu: 'jour' | 'nuit' | 'blanc'; cycler: () => void; choix: Theme };
  ouvrirPlanning: () => void;
}) {
  const jour = aujourdhuiDans(fuseau);
  const etat = useDonnees(
    (signal) => api.planning({ du: jour, au: jour }, signal),
    `${jour}:${version}`,
  );

  // Cocher depuis Aujourd'hui reste possible (handoff, § Interactions) : la case arrete
  // la propagation, sinon le clic ouvrirait le Planning au lieu de cocher.
  const { basculer } = useCocher(signaler, rafraichir);

  // La meteo est un PLUS silencieux : sans ville (404) ou en panne, l'ecran reste
  // exactement le meme — aucune erreur affichee, l'etat d'erreur est simplement ignore.
  const meteo = useDonnees((signal) => api.meteo(signal), `${jour}:${version}`);

  const contenu: JourPlanning | null = etat.donnees?.days.find((j) => j.date === jour) ?? null;
  // Les ROUTINES sont ecartees ici (spec routines) : cet ecran est une vue de lecture
  // rapide, et son sujet est ce qui sort de l'ordinaire. Le diner de 19 h occupe bien le
  // temps de la journee — il se consulte au Planning, pas ici.
  const evenements = (contenu?.events ?? []).filter((e) => e.estUneRoutine !== true);
  const taches = contenu?.tasks ?? [];
  const rappels = contenu?.reminders ?? [];
  const anniversaires = contenu?.birthdays ?? [];
  // Les taches EN RETARD (etape A de l'audit du 2026-09-09). Une journee qui n'a rien
  // d'autre qu'un retard n'est pas « libre » : c'est le jour ou le rattraper.
  const retards = etat.donnees?.overdueTasks ?? [];
  const total =
    evenements.length + taches.length + rappels.length + anniversaires.length + retards.length;

  return (
    <div
      style={{
        flex: '1 1 0',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        padding: '30px 22px 16px 22px',
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: 1.6,
            textTransform: 'uppercase',
            color: C.tertiaire,
          }}
        >
          {jourEnToutesLettres(jour)}
        </div>
        <button
          type="button"
          className="pressable"
          onClick={theme.cycler}
          aria-label={`Apparence : ${theme.libelle}. Changer.`}
          style={{
            height: 32,
            borderRadius: 999,
            background: C.carte,
            border: `1px solid ${C.bordure}`,
            padding: '0 12px',
            fontSize: 11,
            fontWeight: 800,
            color: C.encre,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          {theme.libelle}
        </button>
      </div>

      <div style={{ padding: '22px 0 4px 0' }}>
        <div
          style={{
            fontFamily: POLICE_TITRE,
            fontWeight: 200,
            fontSize: 52,
            lineHeight: 1.04,
            color: C.encre,
          }}
        >
          {theme.resolu === 'nuit' ? 'Bonsoir' : 'Bonjour'}
          <br />
          {prenom}
        </div>
        {etat.donnees !== null ? (
          <div
            style={{
              fontFamily: POLICE_TITRE,
              fontWeight: 300,
              fontSize: 19,
              color: C.secondaire,
              paddingTop: 12,
            }}
          >
            {accroche(total)}
          </div>
        ) : null}
      </div>

      <CarteMeteo jourCourant={jour} meteo={meteo.donnees} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, paddingTop: 22 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 2px 2px 2px',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              color: C.tertiaire,
            }}
          >
            Ta journée
          </div>
          <button
            type="button"
            onClick={ouvrirPlanning}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              border: 'none',
              background: 'none',
              padding: 0,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 800, color: C.accent }}>Ouvrir le planning</span>
            <ChevronDroite taille={12} couleur={C.accent} epaisseur={2.4} />
          </button>
        </div>

        {etat.chargement && etat.donnees === null ? (
          <Squelette lignes={3} texte="Chargement de ta journée…" />
        ) : etat.erreur !== null && etat.donnees === null ? (
          <Panne message={etat.erreur} onReessayer={etat.recharger} />
        ) : total === 0 ? (
          <Vide
            titre="Journée libre"
            texte="Rien au programme aujourd’hui. Ce que tu valides depuis la boîte apparaîtra ici."
          />
        ) : (
          <div className="entree" style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {anniversaires.map((anniversaire) => (
              <Pilule
                key={anniversaire.id}
                teinte={C.corailTexte}
                titre={`${anniversaire.displayName} a ${anniversaire.age} ans`}
                meta="Anniversaire"
                onOuvrir={ouvrirPlanning}
              />
            ))}

            {evenements.map((evenement) => {
              const { restants, total: nbElements } = resteAFaire(evenement);
              return (
                <Pilule
                  key={evenement.id}
                  teinte={teinteDuJour(jour)}
                  titre={evenement.title}
                  meta={[
                    evenement.allDay ? 'Toute la journée' : heureDe(evenement.startsAt, fuseau),
                    evenement.location,
                    evenement.provenance === 'google' ? 'Google Agenda' : null,
                  ]
                    .filter((part) => part !== null && part !== '')
                    .join(' · ')}
                  prive={evenement.visibility === 'PRIVATE'}
                  compteur={
                    nbElements === 0
                      ? null
                      : restants === 0
                        ? { texte: '✓', fait: true }
                        : { texte: `${restants} à faire`, fait: false }
                  }
                  onOuvrir={ouvrirPlanning}
                />
              );
            })}

            {/* Les retards AVANT les taches du jour : ce sont ceux qu'on a le plus
                oublies, et ils se cochent d'ici — rattraper ne doit pas demander d'aller
                chercher ailleurs. */}
            {retards.map((tache) => {
              const fait = tache.doneAt !== null;
              return (
                <Pilule
                  key={`retard-${tache.id}`}
                  teinte={C.corailTexte}
                  titre={tache.title}
                  meta={`En retard depuis ${depuisCombienDeJours(jourDe(tache.dueAt, fuseau), jour)}`}
                  prive={tache.visibility === 'PRIVATE'}
                  barre={fait}
                  onOuvrir={ouvrirPlanning}
                  cocher={{ fait, basculer: () => basculer('tache', tache.id, !fait) }}
                />
              );
            })}

            {taches.map((tache) => {
              const fait = tache.doneAt !== null;
              return (
                <Pilule
                  key={tache.id}
                  teinte={teinteDuJour(jour)}
                  titre={tache.title}
                  meta="À ne pas oublier"
                  prive={tache.visibility === 'PRIVATE'}
                  barre={fait}
                  onOuvrir={ouvrirPlanning}
                  cocher={{ fait, basculer: () => basculer('tache', tache.id, !fait) }}
                />
              );
            })}

            {rappels.map((rappel) => (
              <Pilule
                key={rappel.id}
                teinte={C.menthe}
                titre={rappel.message}
                meta={`Rappel · ${heureDe(rappel.remindAt, fuseau)}`}
                prive={rappel.visibility === 'PRIVATE'}
                onOuvrir={ouvrirPlanning}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Une pilule : un point de couleur, un titre sur UNE ligne, une meta, un compteur.
 * Jamais de detail — le detail vit au Planning, et la pilule y mene.
 */
function Pilule({
  teinte,
  titre,
  meta,
  prive = false,
  barre = false,
  compteur = null,
  cocher = null,
  onOuvrir,
}: {
  teinte: string;
  titre: string;
  meta: string;
  prive?: boolean;
  barre?: boolean;
  compteur?: { texte: string; fait: boolean } | null;
  /** Present quand la chose se coche : la case remplace le point de couleur. */
  cocher?: { fait: boolean; basculer: () => void } | null;
  onOuvrir: () => void;
}) {
  return (
    <div
      className="pressable"
      onClick={onOuvrir}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOuvrir();
        }
      }}
      aria-label={`${titre} — ouvrir le planning`}
      style={{
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 999,
        padding: cocher === null ? '12px 16px' : '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        textAlign: 'left',
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      {cocher === null ? (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: teinte,
            flexShrink: 0,
          }}
        />
      ) : (
        <button
          type="button"
          aria-pressed={cocher.fait}
          aria-label={`${cocher.fait ? 'Décocher' : 'Cocher'} : ${titre}`}
          // `stopPropagation` : sans lui, cocher ouvrirait le Planning du meme geste.
          onClick={(e) => {
            e.stopPropagation();
            cocher.basculer();
          }}
          style={{
            border: 'none',
            background: 'none',
            padding: 0,
            display: 'flex',
            flexShrink: 0,
            cursor: 'pointer',
          }}
        >
          <Case cochee={cocher.fait} taille={22} />
        </button>
      )}
      <span style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <span
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: C.encre,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textDecoration: barre ? 'line-through' : 'none',
          }}
        >
          {titre}
        </span>
        <span style={{ fontSize: 12, color: C.tertiaire }}>{meta}</span>
      </span>
      {prive ? <Cadenas taille={13} couleur={C.tertiaire} epaisseur={2} /> : null}
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {compteur !== null ? (
          <span
            style={{
              minWidth: 24,
              height: 24,
              borderRadius: 12,
              background: compteur.fait ? C.mentheFond : C.accentDoux,
              color: compteur.fait ? C.menthe : C.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 7px',
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {compteur.fait ? <Coche taille={12} couleur={C.menthe} epaisseur={2.6} /> : compteur.texte}
          </span>
        ) : null}
        <ChevronDroite taille={13} couleur={C.tertiaire} epaisseur={2.4} />
      </span>
    </div>
  );
}

/**
 * La carte meteo : fermee, le maximum du jour ; ouverte, les cinq jours avec leur jauge.
 *
 * Ce qu'elle ne montre PAS, et pourquoi : ni temperature courante, ni humidite, ni vent,
 * ni UV. Le prototype les dessine, mais `apps/api/src/lib/meteo.ts` n'interroge
 * Open-Meteo que sur le quotidien — inventer trois tuiles serait exactement ce que le
 * principe « dire vrai » interdit.
 */
function CarteMeteo({
  jourCourant,
  meteo,
}: {
  jourCourant: string;
  meteo: { ville: string; jours: { date: string; description: string; minCelsius: number; maxCelsius: number }[] } | null;
}) {
  const [ouverte, setOuverte] = useState(false);
  if (meteo === null) return null;
  const duJour = meteo.jours.find((j) => j.date === jourCourant);
  if (duJour === undefined) return null;

  // Les bornes de TOUS les jours affiches : la jauge de chaque ligne se place sur la
  // meme echelle, sinon deux barres identiques diraient deux temperatures differentes.
  const cinq = meteo.jours.slice(0, 5);
  const plancher = Math.min(...cinq.map((j) => j.minCelsius));
  const plafond = Math.max(...cinq.map((j) => j.maxCelsius));
  const etendue = Math.max(plafond - plancher, 1);

  return (
    <button
      type="button"
      onClick={() => setOuverte((o) => !o)}
      aria-expanded={ouverte}
      aria-label={ouverte ? 'Replier la météo' : 'Déplier la météo sur cinq jours'}
      style={{
        background: `linear-gradient(140deg, ${C.carteForte}, ${C.carte})`,
        border: `1px solid ${C.bordure}`,
        borderRadius: 20,
        padding: '16px 18px',
        marginTop: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        width: '100%',
        textAlign: 'left',
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <img
            src={imagePourMeteo(duJour.description)}
            alt=""
            width={44}
            height={44}
            style={{ borderRadius: 10, flexShrink: 0, display: 'block' }}
          />
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0 }}>
            <span
              style={{
                fontFamily: POLICE_TITRE,
                fontWeight: 200,
                fontSize: 40,
                lineHeight: 1,
                color: C.encre,
              }}
            >
              {duJour.maxCelsius}°
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: C.encre,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {meteo.ville}
              </span>
              <span style={{ fontSize: 12, color: C.secondaire }}>
                {duJour.description.charAt(0).toUpperCase() + duJour.description.slice(1)} · min{' '}
                {duJour.minCelsius}°
              </span>
            </span>
          </span>
        </span>
        <span
          style={{
            display: 'flex',
            transform: ouverte ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.25s',
            flexShrink: 0,
          }}
        >
          <ChevronBas taille={14} couleur={C.tertiaire} epaisseur={2.2} />
        </span>
      </span>

      {ouverte ? (
        <span className="entree" style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {cinq.map((prevision) => (
            <span key={prevision.date} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 78, fontSize: 13, fontWeight: 700, color: C.encre }}>
                {prevision.date === jourCourant
                  ? "Aujourd'hui"
                  : jourEnToutesLettres(prevision.date).split(' ')[0]}
              </span>
              <img src={imagePourMeteo(prevision.description)} alt="" width={22} height={22} style={{ display: 'block' }} />
              <span style={{ width: 28, fontSize: 12, color: C.secondaire, textAlign: 'right' }}>
                {prevision.minCelsius}°
              </span>
              <span
                style={{
                  flex: 1,
                  height: 5,
                  borderRadius: 3,
                  background: C.rail,
                  position: 'relative',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    borderRadius: 3,
                    background: 'linear-gradient(90deg, #FFC978, #F27059)',
                    left: `${((prevision.minCelsius - plancher) / etendue) * 100}%`,
                    right: `${((plafond - prevision.maxCelsius) / etendue) * 100}%`,
                  }}
                />
              </span>
              <span style={{ width: 28, fontSize: 12, fontWeight: 700, color: C.encre }}>
                {prevision.maxCelsius}°
              </span>
            </span>
          ))}
        </span>
      ) : null}
    </button>
  );
}
